import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center px-5" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10 justify-center">
          <span className="h-2.5 w-2.5 bg-primary block rotate-45" />
          <span className="font-display font-black text-lg tracking-tight uppercase">
            Fuze<span className="text-primary">Point</span>
          </span>
        </Link>

        <div className="border border-white/10 bg-card p-8 sm:p-10">
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">/ Restricted</span>
          <h1 className="mt-4 font-display font-bold uppercase text-3xl tracking-tight">Admin Access</h1>
          <p className="mt-2 text-foreground/50 text-sm">Sign in to view inquiries.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/50 mb-2 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full bg-background border border-white/15 px-4 py-3.5 focus:border-primary focus:outline-none transition-colors"
                placeholder="admin@fuzepoint.com"
              />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/50 mb-2 block">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full bg-background border border-white/15 px-4 py-3.5 focus:border-primary focus:outline-none transition-colors"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p className="font-mono text-xs text-destructive border border-destructive/40 px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-primary px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white hover:bg-white hover:text-background transition-colors duration-300 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>

        <Link to="/" className="mt-6 block text-center font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/40 hover:text-primary transition-colors">
          ← Back to site
        </Link>
      </div>
    </div>
  );
}
